import React, { useState } from 'react'
import {
  InlineNotification,
  notificationStates,
} from '@/components/common/InlineNotification'

import {
  getAllProductNutrigradeTags,
  getDisplayPriceFromItem,
  productStatus,
} from '@/utils/product'

import useAppContext from '@/hooks/useAppContext'
import useStoreItemAvailability, {
  StoreItemAvailabilityResult,
} from '@/hooks/useStoreItemAvailability'

import { NutriGradeIcon } from '@/components/icons/NutriGrade'
import useGetProductImagePath from '@/hooks/useGetProductImagePath'
import useProductImageState from '@/hooks/useProductImageState'
import { formatDate, formatDayRange } from '@/utils/date'
import { isEmpty } from '@/utils/string'
import {
  DefaultParamType,
  TFnType,
  TranslationKey,
  useTranslate,
} from '@tolgee/react'
import { useParams } from 'next/navigation'
import { imageSize } from './ProductImage'
import { P } from '@/components/common/Typo'
import richTextHtmlParser from '@/components/common/RichText/parser'

const getSeasonalAvailabilityMessage = (
  t: TFnType,
  category: Oms.MenuCategory,
  product: Oms.MenuItem,
  locale: string
): {
  text: string
  subText?: string[]
} | null => {
  const item = category.status === productStatus.AVAILABLE ? product : category
  const {
    collectionStartDate,
    collectionEndDate,
    collectionStartTime,
    collectionEndTime,
    availableDays,
  } = item

  const formattedAvailableDays = availableDays
    ? formatDayRange(availableDays, 'short', t)
    : ''
  const isAlwaysAvailable =
    (availableDays || '').split(',').filter((x: string) => x).length === 7

  if (collectionStartDate && collectionEndDate) {
    return {
      text: t(
        'Only available for orders placed for {collectionStartDate} to {collectionEndDate}',
        {
          collectionStartDate: formatDate({
            t,
            date: collectionStartDate,
            locale: locale || 'en_SG',
            options: { year: 'numeric', month: 'short', day: '2-digit' },
          }),
          collectionEndDate: formatDate({
            t,
            date: collectionEndDate,
            locale: locale || 'en_SG',
            options: { year: 'numeric', month: 'short', day: '2-digit' },
          }),
        }
      ),
    }
  }

  if (collectionStartTime && collectionEndTime) {
    return {
      text: availableDays
        ? t(
            'Only available for orders placed for {collectionStartTime} to {collectionEndTime} on {availableDays}',
            {
              collectionStartTime,
              collectionEndTime,
              availableDays: formattedAvailableDays,
            }
          )
        : t(
            'Only available for orders placed for {collectionStartTime} to {collectionEndTime}',
            item as unknown as Record<string, string>
          ),
    }
  }

  if (!isAlwaysAvailable) {
    return {
      text: availableDays
        ? t('Only available on {availableDays}', {
            availableDays: formattedAvailableDays,
          })
        : t('Sorry, this item is currently unavailable'),
    }
  }

  return null
}

const getStoreItemAvailabilityMessage = (
  t: TFnType<DefaultParamType, string, TranslationKey>,
  storeItemAvailability: StoreItemAvailabilityResult
): {
  text: string
  subText?: string[]
} | null => {
  const {
    selectedStore,
    isUnavailableAtSelectedStore,
    isUnavailableAtAllStores,
    isUnavailableAtSomeStores,
    availableStores,
    isPickup,
    unavailableStores,
  } = storeItemAvailability

  const availableStoresName = availableStores
    .filter((store) => store.open)
    .map((store) => store?.name)
  const unavailableStoresName = unavailableStores
    .filter((store) => store.open)
    .map((store) => store?.name)

  let result = null

  if (isUnavailableAtSelectedStore && selectedStore) {
    result =
      availableStoresName.length === 0
        ? {
            text: t(
              'Sorry, this item is currently out of stock in all stores.'
            ),
          }
        : isPickup
        ? {
            text: t(
              'Sorry, this item is currently out of stock at {storeName}. It is currently available at the following outlets:',
              {
                storeName: selectedStore.name,
              }
            ),
            subText: availableStoresName,
          }
        : {
            text: t('Sorry, this item is currently out of stock.'),
          }
  }

  if (isUnavailableAtSomeStores && !selectedStore) {
    result =
      unavailableStoresName.length > 0 && availableStoresName.length > 0
        ? {
            text: t(
              'Item subject to store availability. It is currently not available at the following outlets:'
            ),
            subText: unavailableStoresName,
          }
        : availableStoresName.length === 0
        ? {
            text: t(
              'Sorry, this item is currently out of stock in all stores.'
            ),
          }
        : null
  }

  if (isUnavailableAtAllStores) {
    result = {
      text: t('Sorry, this item is currently out of stock in all stores.'),
    }
  }

  return result
}

const InlineNotificationText = React.memo(
  ({ text, subText }: { text: string; subText?: string[] }): JSX.Element => {
    const { t } = useTranslate()
    const [showSubText, setShowSubText] = useState(false)

    if (!subText)
      return (
        <InlineNotification
          lightTheme
          text={text}
          state={notificationStates.informative}
        />
      )

    if (subText?.length <= 1) {
      return (
        <InlineNotification
          lightTheme
          text={text}
          state={notificationStates.informative}
          subText={<P className="my-1 ml-4 list-item">{subText[0]}</P>}
        />
      )
    }

    const handleClick = () => {
      setShowSubText(!showSubText)
    }

    return (
      <InlineNotification
        lightTheme
        text={text}
        state={notificationStates.informative}
        subText={
          <>
            {showSubText
              ? subText.map((text, index) => (
                  <P key={index} className="my-1 ml-4 list-item">
                    {text}
                  </P>
                ))
              : subText
                  .filter((text, index) => index <= 1)
                  .map((text, index) => (
                    <P key={index} className="my-1 ml-4 list-item">
                      {text}
                    </P>
                  ))}
            {subText.length > 2 && (
              <P
                onClick={handleClick}
                className="my-1 ml-4 list-item cursor-pointer underline"
              >
                {showSubText ? t('Show less') : t('Show more')}
              </P>
            )}
          </>
        }
      />
    )
  }
)

InlineNotificationText.displayName = 'InlineNotificationText'

interface ProductDescriptionProps {
  productState: Oms.MenuItem
  category: Oms.MenuCategory
  noImage?: boolean
}

const ProductDescription = React.memo(
  ({
    category,
    productState: product,
    noImage,
  }: ProductDescriptionProps): JSX.Element => {
    const params = useParams()
    const { t } = useTranslate()

    const message =
      getStoreItemAvailabilityMessage(
        t,
        useStoreItemAvailability({ product, category })
      ) ||
      getSeasonalAvailabilityMessage(
        t,
        category,
        product,
        params?.locale as string
      )
    const { formatMoney } = useAppContext()
    const { preposition, displayPrice } = getDisplayPriceFromItem(
      t,
      product,
      formatMoney
    )
    const { getProductImagePath } = useGetProductImagePath()

    const { showImage } = useProductImageState()

    const productNutrigradeTags = getAllProductNutrigradeTags(product.tags)

    const imageFullUrls =
      product.imageFullUrls?.filter((image: string) => !isEmpty(image))
        ?.length > 0
        ? product.imageFullUrls
        : product.imageUrls?.filter((image: string) => !isEmpty(image))
            ?.length > 0
        ? product.imageUrls
        : [null]
    const allImages = imageFullUrls.map((imageUrl: string | null) =>
      imageUrl
        ? getProductImagePath(imageUrl, false, {
            shouldCache: true,
            containerHeight: 1000,
          })
        : null
    )

    const images = typeof window !== 'undefined' ? allImages : [allImages[0]]

    return (
      <div
        className={`p-2.5 ${
          noImage ? 'lg:pl-0 lg:pt-5' : 'lg:pl-10 lg:pt-10'
        } ${showImage ? `lg:w-[calc(100%-${imageSize}px)]` : ''}`}
      >
        <h1 className="inline text-2xl font-bold">{product.name}</h1>
        {(!showImage || !images[0]) && (product?.tags?.length || 0) > 0 && (
          <>
            <br />
            <NutriGradeIcon tags={product.tags} height={50} width={89} />
          </>
        )}
        {(productNutrigradeTags || [])?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 py-2.5">
            {productNutrigradeTags?.map((tag) => (
              <div
                key={tag}
                className="font-roboto inline-block rounded border border-gray-400 bg-white px-1.5 py-1 text-sm text-black"
              >
                {tag}
              </div>
            ))}
          </div>
        )}
        {message?.text && (
          <div className="mb-6 mt-4">
            <InlineNotificationText
              text={message.text}
              subText={message.subText}
            />
          </div>
        )}
        {product.description && product.description !== '<p></p>' && (
          <div className="my-2 max-w-none opacity-80 [&>p]:my-3">
            {richTextHtmlParser(product.description, {
              withoutContainer: true,
            })}
          </div>
        )}
        <P className="mb-0 mt-2 font-bold lg:my-2">{`${preposition} ${displayPrice}`}</P>
      </div>
    )
  }
)

ProductDescription.displayName = 'ProductDescription'

export default ProductDescription
